import { FieldSet } from '@common/components/forms';
import { CancelAndEditActions } from '@common/components/forms/CancelAndEditActions';
import { ToggleButton } from '@common/components/forms/controls/ToggleButton';
import { PageCard, PageCardProps } from '@common/components/layout';
import { YesNoConfirmModal } from '@common/components/modals';
import { useApp, useUnsavedChangesWarning } from '@common/hooks';
import { AuthOperationUINames } from '@common/models/Enums/AuthOperationStrings';
import { HospitalModel } from '@common/models/HospitalModel';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';

type LocationFlagDetailProps = {
    hospital: HospitalModel,
    onUpdateHospital: (data: HospitalModel) => void
} & PageCardProps



export const LocationFlagDetail = observer(({ hospital, onUpdateHospital, ...cardProps }: LocationFlagDetailProps) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [flagsDirty, setFlagsDirty] = React.useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
    const formId = 'locationHospitalFlagForm';
    const { configuration } = useApp();
    const {
        register, handleSubmit, reset, formState: { isDirty }
    } = useFormContext<Omit<HospitalModel, "defaultScheduleTemplate">>()

    const cancelEditing = React.useCallback(() => {
        setIsEditing(false);
        setShowCancelConfirm(false);
        reset()
    }, [reset]);

    const canToggleDoubleBooking = configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanToggleDoubleBooking);

    const cancelingEditing = React.useCallback(() => {
        if (flagsDirty)
            setShowCancelConfirm(true);
        else
            cancelEditing();
    }, [cancelEditing, flagsDirty]);

    const unsavedChangesPrompt = useUnsavedChangesWarning(flagsDirty);

    React.useEffect(() => {
        if (setFlagsDirty)
            setFlagsDirty(isDirty);
    }, [isDirty, setFlagsDirty]);

    return (
        <PageCard title="Hospital Flags"
            actionElement={canToggleDoubleBooking ?
                <CancelAndEditActions
                    formId={formId}
                    editText="Edit Details"
                    saveText="Save Details"
                    isEditing={isEditing}
                    saveClicked={(e) => {
                        if (!isEditing) {
                            e?.preventDefault();
                            return false;
                        }
                        return true;
                    }}
                    cancelClicked={cancelingEditing}
                    editClicked={() => {
                        setIsEditing(true);
                    }}
                /> : undefined}
            {...cardProps}>
            {unsavedChangesPrompt}
            <YesNoConfirmModal
                visible={showCancelConfirm}
                title="Cancel Changes"
                description="You have unsaved changes, you are sure you want to cancel?"
                busy={false}
                cancelButtonText='No'
                onConfirm={() => cancelEditing()}
                onCancel={() => { setIsEditing(true); setShowCancelConfirm(false) }}
            />
            <Form id={formId} className="input-form" onSubmit={handleSubmit(onUpdateHospital)}>
                <div className="hours-operation">
                    {
                        !canToggleDoubleBooking &&
                        <div className="notifications">
                            <i className="fa fa-exclamation-triangle"></i>
                            <label className="ps-1">To disable Double Booking at this location, please contact a member of field leadership</label>
                        </div>
                    }
                    <FieldSet supportDisabledBackdrop={!canToggleDoubleBooking} disabled={!isEditing || !canToggleDoubleBooking}>
                        <div className="hours-operation-row d-flex flex-row justify-content-start align-items-center p-2">
                            <Form.Label className="mb-0 mt-2 me-2 p-0">Allow Double Booking:</Form.Label>
                            <ToggleButton className="" {...register("isDoubleBookingEnabled")} noSlider={!isEditing || !canToggleDoubleBooking} defaultChecked={hospital.hospitalFlags.isDoubleBookingEnabled} />
                            <hr />
                        </div>
                    </FieldSet>
                </div>
            </Form>
        </PageCard>
    )
});
